import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, RefreshCcw } from "lucide-react";
import { Modal, Button } from "react-bootstrap";
import MyContext from "../../../MyContext";
import "../Inventory/InventoryAccordion.css";

const SellOnInstamart = ({ userID }) => {
  const { baseUrl, config } = useContext(MyContext);

  const [loading, setLoading] = useState(false);
  const [catalogueStore, setCatalogueStore] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const allcatalogueAPI = async (preserveScroll = false) => {
    const currentScrollY = preserveScroll ? window.scrollY || 0 : null;

    setLoading(true);
    try {
      const requestBody = {
        userid: userID,
        secret: "string",
      };

      const response = await axios.post(
        `${baseUrl}/api/Order/v1.0/getCatalogueItemsMaster`,
        requestBody,
        config
      );

      if (response.data.responseCode === "00") {
        setCatalogueStore(response.data.response || []);
      } else {
        setCatalogueStore([]);
      }
    } catch (error) {
      console.error("Catalogue API error:", error);
      setCatalogueStore([]);
    } finally {
      setLoading(false);
      if (preserveScroll && currentScrollY !== null) {
        window.scrollTo({ top: currentScrollY, behavior: "auto" });
      }
    }
  };

  useEffect(() => {
    allcatalogueAPI();
  }, [userID]);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return catalogueStore;

    return catalogueStore
      .map((category) => {
        const filteredItems = (category.menu_items || []).filter((item) => {
          const firstPrice = item?.prices?.[0];
          return (
            String(category.category_name || "").toLowerCase().includes(q) ||
            String(item.item_name || "").toLowerCase().includes(q) ||
            String(item.item_id || "").toLowerCase().includes(q) ||
            String(firstPrice?.barcodeNumber || "").toLowerCase().includes(q)
          );
        });

        return {
          ...category,
          menu_items: filteredItems,
        };
      })
      .filter((category) => category.menu_items.length > 0);
  }, [catalogueStore, search]);

  const canSelectItem = (item) => {
    return !(
      item?.is_sync === "Y" ||
      item?.is_sync === 1 ||
      item?.is_sync === true
    );
  };

  const toggleItem = (item) => {
    if (!canSelectItem(item)) return;

    setSelectedItems((prev) => ({
      ...prev,
      [item.item_id]: !prev[item.item_id],
    }));
  };

  const toggleCategoryItems = (category) => {
    const selectableItems = (category.menu_items || []).filter((item) =>
      canSelectItem(item)
    );

    const allSelected =
      selectableItems.length > 0 &&
      selectableItems.every((item) => selectedItems[item.item_id]);

    const updated = { ...selectedItems };

    selectableItems.forEach((item) => {
      updated[item.item_id] = !allSelected;
    });

    setSelectedItems(updated);
  };

  const allVisibleItems = useMemo(() => {
    return filteredCategories.flatMap((category) => category.menu_items || []);
  }, [filteredCategories]);

  const selectedSyncItems = useMemo(() => {
    return allVisibleItems.filter(
      (item) => canSelectItem(item) && selectedItems[item.item_id]
    );
  }, [allVisibleItems, selectedItems]);

  const hasSelectedItems = selectedSyncItems.length > 0;

  const syncPayload = useMemo(() => {
    return {
      fk_userid: String(userID),
      itemlist: selectedSyncItems.map((item) => ({
        itemid: String(item.item_id),
      })),
    };
  }, [selectedSyncItems, userID]);

  const handleConfirmSync = async () => {
    try {
      setSyncLoading(true);

      const response = await axios.post(
        `https://api.anlook.com/api/Order/SyncCatalogItemInstamart`,
        syncPayload,
        config
      );

      console.log("Sync response:", response.data);

      setShowConfirmModal(false);
      setSelectedItems({});
      await allcatalogueAPI(true);
    } catch (error) {
      console.error("Sync API error:", error);
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <div className="inv-industrial-wrap">
      <div className="inv-industrial-head">
        <div className="inv-industrial-title">
          <h2 className="CreateCampaign">Sell on Instamart</h2>
        </div>

        <div
          className="inv-industrial-actions"
          style={{ display: "flex", alignItems: "center", gap: "10px" }}
        >
          <div className="inv-industrial-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search category / item / barcode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button
            className="inv-btn inv-btn-ghost"
            onClick={() => allcatalogueAPI(true)}
            title="Refresh"
            type="button"
          >
            <RefreshCcw size={16} />
          </button>

          <button
            type="button"
            className="btn btn-success"
            disabled={!hasSelectedItems}
            onClick={() => setShowConfirmModal(true)}
          >
            Sync to Instamart
          </button>
        </div>
      </div>

      {loading ? (
        <div className="inv-loading-wrap">
          <div className="inv-loading-card">
            <div className="inv-spinner" />
            <div className="inv-loading-text">Loading catalogue…</div>
          </div>
        </div>
      ) : filteredCategories.length === 0 ? (
        <div className="inv-industrial-card">
          <div className="inv-industrial-empty">No catalogue data found.</div>
        </div>
      ) : (
        filteredCategories.map((category, index) => {
          const items = category.menu_items || [];
          const selectableItems = items.filter((item) => canSelectItem(item));
          const selectedCount = selectableItems.filter(
            (item) => selectedItems[item.item_id]
          ).length;

          return (
            <div
              className="inv-industrial-card"
              key={`${category.cat_id || category.category_name}-${index}`}
              style={{ marginBottom: "20px" }}
            >
              <div
                style={{
                  padding: "16px 18px",
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {category.category_name || "Unnamed Category"}
                  </h3>
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "13px",
                      color: "#6b7280",
                    }}
                  >
                    {items.length} items • {selectedCount} selected
                  </p>
                </div>

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: selectableItems.length ? "pointer" : "not-allowed",
                    opacity: selectableItems.length ? 1 : 0.5,
                  }}
                >
                  <input
                    type="checkbox"
                    disabled={!selectableItems.length}
                    checked={
                      selectableItems.length > 0 &&
                      selectableItems.every((item) => selectedItems[item.item_id])
                    }
                    onChange={() => toggleCategoryItems(category)}
                  />
                  Select all
                </label>
              </div>

              <div className="inv-industrial-table">
                <div className="inv-industrial-row inv-industrial-headrow">
                  <div>Select</div>
                  <div>Image</div>
                  <div>Name</div>
                  <div>Item ID</div>
                  <div>Price</div>
                  <div>Barcode</div>
                  <div>Status</div>
                </div>

                {items.map((item) => {
                  const firstPrice = item?.prices?.[0] || {};
                  const selectable = canSelectItem(item);

                  return (
                    <div className="inv-industrial-row" key={item.item_id}>
                      <div>
                        <input
                          type="checkbox"
                          disabled={!selectable}
                          checked={!!selectedItems[item.item_id]}
                          onChange={() => toggleItem(item)}
                        />
                      </div>

                      <div className="inv-industrial-imgcell">
                        {item?.photo ? (
                          <img
                            src={item.photo}
                            alt={item.item_name}
                            className="inv-industrial-thumb"
                          />
                        ) : (
                          <div className="inv-industrial-thumb ph">No Img</div>
                        )}
                      </div>

                      <div className="inv-industrial-name">
                        <div className="inv-industrial-name-main">
                          {item.item_name || "—"}
                        </div>
                        <div className="inv-industrial-name-sub">
                          {item.item_description || "No description"}
                        </div>
                      </div>

                      <div>{item.item_id || "—"}</div>
                      <div>₹{firstPrice?.price ?? "0"}</div>
                      <div>{firstPrice?.barcodeNumber || "—"}</div>

                      <div>
                        <span
                          style={{
                            background: selectable ? "#fef3c7" : "#dcfce7",
                            color: selectable ? "#92400e" : "#166534",
                            borderRadius: "999px",
                            padding: "4px 10px",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          {selectable ? "Pending Sync" : "Synced to Chaafo"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      <Modal
        show={showConfirmModal}
        onHide={() => !syncLoading && setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton={!syncLoading}>
          <Modal.Title>Confirm Sync</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>Are you sure you want to sync these items to Instamart?</p>

          <div
            style={{
              maxHeight: "250px",
              overflowY: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "10px",
              background: "#f9fafb",
            }}
          >
            {selectedSyncItems.map((item) => (
              <div
                key={item.item_id}
                style={{
                  padding: "8px 0",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <div style={{ fontWeight: 600 }}>{item.item_name}</div>
                <div style={{ fontSize: "13px", color: "#6b7280" }}>
                  Item ID: {item.item_id}
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
            disabled={syncLoading}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmSync}
            disabled={syncLoading || !hasSelectedItems}
          >
            {syncLoading ? "Syncing..." : "Confirm Sync"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SellOnInstamart;